import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';
import {
  ApiResponse,
  TenantUserDto,
  TenantAdminDto,
  TenantSearchDto,
} from '../../models';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { uiLocale } from '../../common/i18n/i18n-helpers';
import { LanguageService } from '../../services/language.service';
import { TenantUserService } from '../../services/tenant-user.service';

@Component({
  selector: 'app-tenant-list',
  templateUrl: './tenant-list.component.html',
  styleUrl: './tenant-list.component.scss',
})
export class TenantListComponent implements OnInit {
  registrationForm: FormGroup;
  @ViewChild('confirmDialog') confirmDialog!: ConfirmDialogComponent;
  tenantusersDto: TenantUserDto[] = [];
  filteredtenantusersDto: TenantUserDto[] = [];
  searchTerm: string = '';
  selectedtenantusersDto: TenantUserDto | null = null;
  isDetailsModalShow: boolean = false;
  isAddEditModalShow: boolean = false;
  isLoading: boolean = true;
  totalItems: number = 0;
  tenId: string = '';
  techId: string = '';
  protected searchCriteria: TenantSearchDto = {
    pageNumber: 1,
    pageSize: 5,
    sortingByColumn: 'nameEn',
    sortingType: 1,
    isExport: false,
    nameAr: '',
    nameEn: '',
  };

  tenantuserColumns = [
    { key: 'fullNameEn', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    { key: 'dateOfBirthDisplay', label: 'Date of Birth' },
    { key: 'genderId', label: 'Gender' },
  ];

  // Readable date in the UI language, or an em-dash when missing / the .NET DateTime.MinValue default.
  formatDob(value: unknown): string {
    if (!value) return '—';
    const d = new Date(value as string);
    if (isNaN(d.getTime()) || d.getFullYear() <= 1) return '—';
    return d.toLocaleDateString(uiLocale(this.language.current), { day: '2-digit', month: 'short', year: 'numeric' });
  }

  constructor(
    private fb: FormBuilder,
    private tenantusersDtoSrv: TenantUserService,
    private toastr: ToastrService,
    private language: LanguageService,
    private auth: AuthService
  ) {
    this.registrationForm = this.fb.group(
      {
        fullNameAr: ['', [Validators.required, Validators.minLength(3)]],
        fullNameEn: ['', [Validators.required, Validators.minLength(3)]],
        email: ['', [Validators.required, Validators.email]],
        dateOfBirth: ['', [Validators.required]],
        genderId: ['', [Validators.required]],
        tenantId: ['', [Validators.required]],
        phoneNumber: [
          '',
          [Validators.required, Validators.pattern('^\\+?[0-9]{10,15}$')],
        ],
        userName: ['', [Validators.required, Validators.minLength(3)]],
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]],
        nameAr: ['', [Validators.required, Validators.minLength(3)]],
        nameEn: ['', [Validators.required, Validators.minLength(3)]],
        url: ['', [Validators.required, Validators.minLength(3)]],
        descriptionAr: ['', [Validators.minLength(3)]],
        descriptionEn: ['', [Validators.minLength(3)]],
      },
      { validator: this.passwordMatchValidator }
    );
  }

  ngOnInit(): void {
    this.getPagedTenantUsers(1);
  }

  get teacherList(): TenantAdminDto[] {
    return this.filteredtenantusersDto.map(
      (tu) =>
        ({
          ...tu.teacher,
          dateOfBirthDisplay: this.formatDob(tu.teacher?.dateOfBirth),
        } as TenantAdminDto)
    );
  }

  private findTenantUser(teacher: TenantAdminDto): TenantUserDto | null {
    return this.filteredtenantusersDto.find((tu) => tu.teacher.id === teacher.id) ?? null;
  }

  applyFilter() {
    const search = this.searchTerm.toLowerCase();
    this.filteredtenantusersDto = this.tenantusersDto.filter(
      (tu) =>
        (tu.teacher.fullNameEn ?? '').toLowerCase().includes(search) ||
        (tu.teacher.email ?? '').toLowerCase().includes(search) ||
        (tu.teacher.phoneNumber ?? '').includes(search)
    );
  }

  /** Creating and deleting tenants is reserved for the platform Administrator (the API enforces it too). */
  get isPlatformAdmin(): boolean {
    return this.auth.hasAnyRole('Administrator');
  }

  deleteTenantUsers(teacher: TenantAdminDto): void {
    if (!this.isPlatformAdmin) return;
    const tenantUser = this.findTenantUser(teacher);
    if (tenantUser) {
      this.selectedtenantusersDto = tenantUser;
      this.confirmDialog.openModal();
    }
  }

  onDeleteConfirmed(confirmed: boolean): void {
    if (!confirmed || !this.selectedtenantusersDto?.tenant.id) return;
    this.isLoading = true;
    this.tenantusersDtoSrv
      .deleteTenantUser(this.selectedtenantusersDto.tenant.id)
      .subscribe({
        next: (result) => {
          if (result.isSuccess) {
            this.getPagedTenantUsers(1);
            this.toastr.success(
              `Tenant "${this.selectedtenantusersDto?.tenant?.nameEn}" deleted successfully.`
            );
          } else {
            this.toastr.error('Unexpected response format.');
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error deleting tenant user:', error);
          this.toastr.error(
            `Failed to delete tenant: ${this.selectedtenantusersDto?.tenant?.nameEn}`
          );
        },
      });
  }

  viewTenantUsersDetails(teacher: TenantAdminDto) {
    const tenantUser = this.findTenantUser(teacher);
    if (this.selectedtenantusersDto === tenantUser) {
      this.selectedtenantusersDto = null;
      this.isDetailsModalShow = false;
    } else {
      this.selectedtenantusersDto = tenantUser;
      this.isDetailsModalShow = true;
    }
  }

  AddEditModalShow(item: TenantAdminDto | null) {
    this.isAddEditModalShow = true;
    if (item) {
      const tenantUser = this.findTenantUser(item);
      if (tenantUser) {
        this.selectedtenantusersDto = tenantUser;
        this.tenId = tenantUser.tenant.id ?? '';
        this.techId = tenantUser.teacher.id ?? '';
        // Password not required for edit
        this.registrationForm.get('password')?.clearValidators();
        this.registrationForm.get('confirmPassword')?.clearValidators();
        this.registrationForm.get('password')?.updateValueAndValidity();
        this.registrationForm.get('confirmPassword')?.updateValueAndValidity();
        this.setTenantUsersData();
      }
    } else {
      this.techId = '';
      this.tenId = '';
      this.registrationForm.reset();
      // Re-apply password validators for create
      this.registrationForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
      this.registrationForm.get('confirmPassword')?.setValidators([Validators.required]);
      this.registrationForm.get('password')?.updateValueAndValidity();
      this.registrationForm.get('confirmPassword')?.updateValueAndValidity();
    }
  }

  closeModal() {
    this.selectedtenantusersDto = null;
    this.isDetailsModalShow = false;
    this.isAddEditModalShow = false;
  }

  onPageChange(page: number): void {
    this.searchCriteria.pageNumber = page;
    this.getPagedTenantUsers(page);
  }

  onSubmit() {
    this.isLoading = true;

    if (this.registrationForm.invalid) {
      this.isLoading = false;
      return;
    }

    this.saveTenantUsers().subscribe({
      next: (lookup: ApiResponse<TenantUserDto>) => {
        if (lookup.isSuccess) {
          this.toastr.success('Tenant user saved successfully!');
          this.getPagedTenantUsers(1);
          this.closeModal();
        } else {
          this.toastr.error('Unexpected response format.');
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error saving tenant user:', error);
        this.toastr.error('Failed to save tenant user');
        this.isLoading = false;
      },
    });
  }

  private saveTenantUsers() {
    const requestData = this.getPostTenantUsersTenantData();
    return this.tenId
      ? this.tenantusersDtoSrv.updateTenantUser(this.tenId, requestData)
      : this.tenantusersDtoSrv.addTenantUser(requestData);
  }

  private getPagedTenantUsers(page: number): void {
    this.isLoading = true;
    const requestPayload = this.updateSearchCriteria(page);

    this.tenantusersDtoSrv.getTenantUsers(requestPayload).subscribe({
      next: (result: ApiResponse<TenantUserDto[]>) => {
        if (result.isSuccess && result.value) {
          this.tenantusersDto = result.value;
          this.filteredtenantusersDto = this.tenantusersDto;
          this.totalItems = result.totalCount;
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error fetching tenant users:', err);
      },
    });
  }

  private updateSearchCriteria(page: number): TenantSearchDto {
    return {
      ...this.searchCriteria,
      pageNumber: page,
    };
  }

  private setTenantUsersData() {
    if (this.selectedtenantusersDto) {
      const { tenant, teacher } = this.selectedtenantusersDto;
      this.registrationForm.patchValue({
        nameAr: tenant.nameAr,
        nameEn: tenant.nameEn,
        url: tenant.url,
        descriptionAr: tenant.descriptionAr,
        descriptionEn: tenant.descriptionEn,
        fullNameAr: teacher.fullNameAr,
        fullNameEn: teacher.fullNameEn,
        email: teacher.email,
        dateOfBirth: teacher.dateOfBirth,
        genderId: teacher.genderId,
        phoneNumber: teacher.phoneNumber,
        userName: teacher.userName,
      });
    }
  }

  private passwordMatchValidator(form: FormGroup) {
    const pw = form.get('password')?.value;
    const cpw = form.get('confirmPassword')?.value;
    if (!pw && !cpw) return null;
    return pw === cpw ? null : { mismatch: true };
  }

  private getPostTenantUsersTenantData(): any {
    const v = this.registrationForm.value;
    if (this.tenId) {
      return {
        tenant: {
          id: this.tenId,
          nameAr: v.nameAr,
          nameEn: v.nameEn,
          url: v.url,
          descriptionAr: v.descriptionAr,
          descriptionEn: v.descriptionEn,
        },
        teacher: {
          id: this.techId,
          fullNameAr: v.fullNameAr,
          fullNameEn: v.fullNameEn,
          genderId: v.genderId ? parseInt(v.genderId) : null,
          phoneNumber: v.phoneNumber,
          dateOfBirth: v.dateOfBirth,
        },
      };
    } else {
      return {
        tenant: {
          nameAr: v.nameAr,
          nameEn: v.nameEn,
          url: v.url,
          descriptionAr: v.descriptionAr,
          descriptionEn: v.descriptionEn,
        },
        teacher: {
          userName: v.userName,
          email: v.email,
          fullNameAr: v.fullNameAr,
          fullNameEn: v.fullNameEn,
          genderId: v.genderId ? parseInt(v.genderId) : null,
          phoneNumber: v.phoneNumber,
          dateOfBirth: v.dateOfBirth,
          password: v.password,
          tenantId: v.tenantId,
        },
      };
    }
  }
}


